import { Fragment, useEffect, useRef, useState } from "react";
import { Input } from "@components";
import { getSalesOrders, getPaymentRequests } from "@api";
import { buildApiQuery, formatAmount } from "@utils";
import { useMerchant } from "@auth";
import { X, ReceiptCutoff } from "react-bootstrap-icons";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

const PrefilPaymentRequestSelect = ({ onSelected, onDeselected, onPaymentRequestSelected }) => {
  const { hasModule } = useMerchant();
  const showSalesOrders = hasModule("SalesOrdersV2");
  const [term, setTerm] = useState("");
  const [salesOrders, setSalesOrders] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [searchRequests, setSearchRequests] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null); // null | "payment" | "salesorder"
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch all sales orders on mount, refresh every 10s (only if module enabled)
  useEffect(() => {
    if (!showSalesOrders) return;
    async function fetchSalesOrders() {
      const data = await getSalesOrders();
      setSalesOrders(data);
    }
    fetchSalesOrders();
    const intervalId = setInterval(fetchSalesOrders, 10000);
    return () => clearInterval(intervalId);
  }, [showSalesOrders]);

  // Fetch 10 most recent payment requests on mount
  useEffect(() => {
    async function fetchRecent() {
      try {
        const query = buildApiQuery({ page: 1, pageSize: 10, sort: "requestedOn", sortDirection: "desc" });
        const data = await getPaymentRequests(query);
        setRecentRequests(data?.data ?? []);
      } catch {
        setRecentRequests([]);
      }
    }
    fetchRecent();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setTerm(value);
    clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSearchRequests([]);
      setOpen(false);
      return;
    }

    setOpen(true);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const query = buildApiQuery({ page: 1, pageSize: 8, searchTerm: value });
        const data = await getPaymentRequests(query);
        setSearchRequests(data?.data ?? []);
      } catch {
        setSearchRequests([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  // Client-side filtered sales orders (exclude already selected, only if module enabled)
  const filteredSalesOrders = showSalesOrders && term.trim()
    ? (salesOrders || [])
        .filter(
          (x) =>
            !selectedOrders.find((s) => s.id === x.id) &&
            (x.customerName.toLowerCase().includes(term.toLowerCase()) ||
              x.grossAmount.toString().includes(term) ||
              (x.remainingAmount ?? "").toString().includes(term) ||
              x.number.toLowerCase().includes(term.toLowerCase())),
        )
        .slice(0, 5)
    : [];

  const displayRequests = term.trim() ? searchRequests : recentRequests;

  const mergedResults = [
    ...displayRequests.map((r) => ({ ...r, _type: "payment", _date: r.requestedOn })),
    ...(term.trim()
      ? filteredSalesOrders.map((x) => ({ ...x, _type: "salesorder", _date: x.createdOn }))
      : []),
  ].sort((a, b) => {
    if (!a._date && !b._date) return 0;
    if (!a._date) return 1;
    if (!b._date) return -1;
    return new Date(b._date) - new Date(a._date);
  });

  const handleSelectSalesOrder = (x) => {
    setOpen(false);
    setTerm("");
    setSearchRequests([]);
    setSelectedOrders((prev) => [...prev, x]);
    onSelected(x);
  };

  const handleDeselectSalesOrder = (x) => {
    setSelectedOrders((prev) => prev.filter((o) => o.id !== x.id));
    if (onDeselected) onDeselected(x);
  };

  const handleSelectPaymentRequest = (request) => {
    setTerm(request.customerName || "");
    setSearchRequests([]);
    setOpen(false);
    setSelectedOrders([]);
    if (onPaymentRequestSelected) onPaymentRequestSelected(request);
  };

  const filteredResults = activeFilter
    ? mergedResults.filter((item) => item._type === activeFilter)
    : mergedResults;

  const handleFilterClick = (type) =>
    setActiveFilter((current) => (current === type ? null : type));

  return (
    <Fragment>
      <div ref={containerRef} className="relative">
        <Input
          value={term}
          onChange={handleSearch}
          placeholder="Search by reference, amount or customer name"
          autoComplete="off"
        />
        {open && (
          <div className="absolute text-sm w-full bg-white border z-50 shadow-md max-h-72 overflow-y-auto">
            {showSalesOrders && (
              <div className="sticky top-0 bg-white border-b border-zinc-100 px-3 py-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleFilterClick("salesorder")}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors ${activeFilter === "salesorder" ? "bg-primary-500/20 text-primary-500" : "bg-white text-zinc-700 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50"}`}
                >
                  <ReceiptCutoff size={11} />
                  Quotes / Sales orders
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterClick("payment")}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors ${activeFilter === "payment" ? "bg-primary-500/20 text-primary-500" : "bg-white text-zinc-700 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50"}`}
                >
                  <PaperAirplaneIcon aria-hidden="true" className="size-3" />
                  Past requests
                </button>
              </div>
            )}

            {loading && (
              <div className="p-3 text-zinc-400 text-center">Searching…</div>
            )}

            {!loading && filteredResults.length === 0 && (
              <div className="p-3 text-zinc-400 text-center">No results found</div>
            )}

            {!loading && filteredResults.map((item) =>
              item._type === "salesorder" ? (
                <div
                  key={item.id}
                  role="button"
                  onClick={() => handleSelectSalesOrder(item)}
                  className="p-3 flex items-center gap-3 hover:bg-zinc-50 cursor-pointer"
                >
                  <ReceiptCutoff size={14} className="shrink-0 text-zinc-400" />
                  <span className="font-medium text-zinc-900">{item.number}</span>
                  <span className="text-zinc-500">{item.customerName}</span>
                  <span className="ml-auto text-zinc-700 font-medium">
                    {formatAmount(item.remainingAmount ?? item.grossAmount)}
                  </span>
                </div>
              ) : (
                <div
                  key={item.id}
                  role="button"
                  onClick={() => handleSelectPaymentRequest(item)}
                  className="p-3 flex items-start gap-3 hover:bg-zinc-50 cursor-pointer"
                >
                  <PaperAirplaneIcon aria-hidden="true" className="size-3.5 shrink-0 text-zinc-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-zinc-900">{item.customerName}</span>
                      <span className="text-zinc-500">{item.reference}</span>
                      <span className="ml-auto text-zinc-700 font-medium">
                        {formatAmount(item.amountRequested)}
                      </span>
                    </div>
                    {(item.recipientEmail || item.recipientPhoneNumber) && (
                      <p className="text-zinc-400 text-xs mt-0.5 truncate">
                        {item.recipientEmail || item.recipientPhoneNumber}
                      </p>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Selected sales order chips */}
      {selectedOrders.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => handleDeselectSalesOrder(order)}
              role="button"
              className="flex items-center gap-2 border border-gray-200 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded-sm text-sm"
            >
              <span>{order.number}</span>
              <span>·</span>
              <span>{formatAmount(order.remainingAmount ?? order.grossAmount)}</span>
              <X size={14} />
            </div>
          ))}
        </div>
      )}
    </Fragment>
  );
};

export default PrefilPaymentRequestSelect;
